import cx from 'classnames';
import Image from 'next/image';
import { useCallback } from 'react';

import { useTrackEventAmplitude } from '../../../core/Tracking/Amplitude/Amplitude';
import { Button } from '../../../design-system/components/form/button/Button';
import { ImageCopyright } from '../../../design-system/components/image/copyright/ImageCopyright';
import { useModal } from '../../../design-system/components/modal/useModal';
import { Typography } from '../../../design-system/components/typography/Typography';
import { DestinationDetailDocument } from '../../../domains/Prismic/DestinationDetailDocument';
import { ModalDownloadGuide } from '../../shared/marketing/ModalDownloadGuide';

import style from './Cover.module.css';

type CoverProps = {
    title: DestinationDetailDocument['title'];
    supportingTitle: DestinationDetailDocument['supportingTitle'];
    link: DestinationDetailDocument['link'];
    modalCtaLabel: DestinationDetailDocument['modalCtaLabel'] | null;
    cover: DestinationDetailDocument['cover'];
    coverCopyright: DestinationDetailDocument['coverCopyright'] | null;
    newsletterModal: {
        picture: DestinationDetailDocument['newsletterModal']['picture'] | null;
        title: DestinationDetailDocument['newsletterModal']['title'] | null;
        description: DestinationDetailDocument['newsletterModal']['description'] | null;
        additionalCheckboxLabel:
            | DestinationDetailDocument['newsletterModal']['additionalCheckboxLabel']
            | null;
        additionalCheckboxRequired: DestinationDetailDocument['newsletterModal']['additionalCheckboxRequired'];
        optinLabel: DestinationDetailDocument['newsletterModal']['optinLabel'] | null;
        emailLabel: DestinationDetailDocument['newsletterModal']['emailLabel'] | null;
        emailPlaceholder: DestinationDetailDocument['newsletterModal']['emailPlaceholder'] | null;
        ctaLabel: DestinationDetailDocument['newsletterModal']['ctaLabel'] | null;
        tagSource: DestinationDetailDocument['newsletterModal']['tagSource'] | null;
        confirmationTitle: DestinationDetailDocument['newsletterModal']['confirmationTitle'] | null;
        confirmationDescription:
            | DestinationDetailDocument['newsletterModal']['confirmationDescription']
            | null;
        confirmationCta: DestinationDetailDocument['newsletterModal']['confirmationCta'] | null;
    } | null;
};

export function Cover({
    title,
    supportingTitle,
    link,
    modalCtaLabel,
    cover,
    coverCopyright,
    newsletterModal,
}: CoverProps) {
    const { trackEvent } = useTrackEventAmplitude();
    const modalProps = useModal();

    const handleClickButton = useCallback(() => {
        if (newsletterModal) {
            modalProps.open();
        }
        trackEvent((ampli, defaultProperties) => {
            ampli.marketingDestinationDetailsCtaClicked({
                ...defaultProperties,
                block: 'cover',
                url_redirection: link.url ?? '',
                row_number: 0,
            });
        });
    }, [link.url, modalProps, newsletterModal, trackEvent]);

    return (
        <div className={style.cover}>
            <div className={style.imageWrapper}>
                {cover.url ? (
                    <Image
                        className={style.image}
                        alt={cover.alt}
                        src={cover.url}
                        unoptimized
                        fill
                        priority
                    />
                ) : null}

                {coverCopyright ? <ImageCopyright copyright={coverCopyright} /> : null}
            </div>
            <div className={style.contentWrapper}>
                <span className={cx(style.date, 'font-body-bold')}>{supportingTitle}</span>

                <Typography className={style.title} as="h1" scale="primary-xl-extra-bold">
                    {title}
                </Typography>

                {(link.url && link.label) || (newsletterModal && modalCtaLabel) ? (
                    <Button
                        href={newsletterModal ? undefined : (link?.url ?? undefined)}
                        target="_blank"
                        className={style.registerButton}
                        onClick={handleClickButton}
                    >
                        {link.label || modalCtaLabel}
                    </Button>
                ) : null}
            </div>
            {newsletterModal ? (
                <ModalDownloadGuide
                    {...modalProps}
                    picture={
                        newsletterModal.picture ?? {
                            url: '',
                            alt: '',
                        }
                    }
                    title={newsletterModal.title ?? ''}
                    description={newsletterModal.description ?? ''}
                    optinLabel={newsletterModal.optinLabel ?? ''}
                    additionalCheckboxLabel={newsletterModal.additionalCheckboxLabel ?? undefined}
                    additionalCheckboxRequired={newsletterModal.additionalCheckboxRequired ?? false}
                    emailLabel={newsletterModal.emailLabel ?? ''}
                    emailPlaceholder={newsletterModal.emailPlaceholder ?? ''}
                    buttonLink={link?.url}
                    buttonLabel={newsletterModal.ctaLabel ?? ''}
                    tagSource={newsletterModal.tagSource ?? ''}
                    confirmationTitle={newsletterModal.confirmationTitle ?? ''}
                    confirmationDescription={newsletterModal.confirmationDescription ?? undefined}
                    confirmationCta={newsletterModal.confirmationCta ?? undefined}
                />
            ) : null}
        </div>
    );
}
